package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.skeleton_shift_template_deleted
import evoshift.composeapp.generated.resources.skeleton_shift_template_deleted_from_day
import com.evoapps.evoshift.domain.usecase.withoutShiftTemplate
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.DayTemplate
import com.evoapps.evoshift.domain.model.ShiftTemplate
import com.evoapps.evoshift.domain.usecase.AddShiftToDayTemplateUseCase
import com.evoapps.evoshift.domain.usecase.CreateDayTemplateUseCase
import com.evoapps.evoshift.domain.usecase.DeleteDayTemplateUseCase
import com.evoapps.evoshift.domain.usecase.DeleteShiftTemplateUseCase
import com.evoapps.evoshift.domain.usecase.DuplicateDayTemplateUseCase
import com.evoapps.evoshift.domain.usecase.GetSkeletonEditorDataUseCase
import com.evoapps.evoshift.domain.usecase.GetWorkplaceUseCase
import com.evoapps.evoshift.domain.usecase.RemoveShiftFromDayTemplateUseCase
import com.evoapps.evoshift.domain.usecase.RenameDayTemplateUseCase
import com.evoapps.evoshift.domain.usecase.UpdateShiftTemplateUseCase
import com.evoapps.evoshift.domain.usecase.UpdateWeeklySkeletonUseCase
import com.evoapps.evoshift.domain.usecase.settings.CreateShiftTemplateUseCase
import com.evoapps.evoshift.domain.util.normalizeTime
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class SkeletonEditorUiState(
    val isLoading: Boolean = true,
    val error: UiText? = null,
    /** SH-190: a short note after a change, e.g. "Shift template "Late" deleted." (shown in the picker). */
    val notice: UiText? = null,

    // Kontekst
    val workplaceId: String = "",

    // 1. GŁÓWNA MAPA: Dzień Tygodnia (1..7) -> DayTemplateId
    // To jest to, co finalnie zapisujemy w WorkplaceSettings
    val weekSkeleton: Map<Int, String?> = emptyMap(),

    // 2. SŁOWNIKI (Do wyświetlania detali na UI)
    val availableDayTemplates: List<DayTemplate> = emptyList(),
    val availableShiftTemplates: List<ShiftTemplate> = emptyList(),
    val availableRoles: List<String> = emptyList(),
    val availableSkills: List<String> = emptyList(),

    // UI Helper: Czy edytujemy konkretny szablon dnia?
    val selectedDayTemplateId: String? = null,

    // UI Helper: Whether showing list of all shift templates
    val isManagingShiftTemplates: Boolean = false,

    val firstDayOfWeek: Int = 1
) {
    // Helper do pobrania pełnego obiektu szablonu dla danego dnia tygodnia
    fun getTemplateForDay(dayIso: Int): DayTemplate? {
        val templateId = weekSkeleton[dayIso]
        return availableDayTemplates.find { it.id == templateId }
    }

    // Helper do rozwinięcia ID zmian w konkretne obiekty (dla widoku edycji)
    /** SH-190: the day templates that use the shift template [shiftTemplateId], and how many times each. */
    fun dayTemplatesUsing(shiftTemplateId: String): List<Pair<String, Int>> =
        availableDayTemplates.mapNotNull { day ->
            day.shiftTemplateIds.count { it == shiftTemplateId }.takeIf { it > 0 }?.let { day.name to it }
        }

    fun getShiftsForSelectedTemplate(): List<ShiftTemplate> {
        val template =
            availableDayTemplates.find { it.id == selectedDayTemplateId } ?: return emptyList()
        return template.shiftTemplateIds.mapNotNull { id ->
            availableShiftTemplates.find { it.id == id }
        }
    }
}


sealed interface SkeletonEditorEvent {
    // Inicjalizacja
    data class LoadData(val workplaceId: String) : SkeletonEditorEvent

    // --- POZIOM 1: TYDZIEŃ (Przypisanie szablonu do Poniedziałku, Wtorku...) ---
    data class AssignTemplateToDayOfWeek(
        val dayIso: Int,
        val dayTemplateId: String? // null = dzień wolny
    ) : SkeletonEditorEvent

    // --- POZIOM 2: DZIEŃ (Tworzenie/Edycja kontenerów) ---
    data class CreateDayTemplate(val name: String) : SkeletonEditorEvent
    data class SelectDayTemplateForEditing(val templateId: String?) :
        SkeletonEditorEvent // Otwiera dialog/panel boczny

    data class RenameDayTemplate(val templateId: String, val newName: String) : SkeletonEditorEvent
    data class DeleteDayTemplate(val templateId: String) : SkeletonEditorEvent
    data class DuplicateDayTemplate(val templateId: String) : SkeletonEditorEvent

    // --- POZIOM 3: ZMIANY (Dodawanie zmian do Szablonu Dnia) ---
    // Dodaje istniejący typ zmiany (np. "Rano") do szablonu dnia (np. "Standard")
    data class AddShiftToDayTemplate(
        val dayTemplateId: String,
        val shiftTemplateId: String
    ) : SkeletonEditorEvent

    data class RemoveShiftFromDayTemplate(
        val dayTemplateId: String,
        val shiftTemplateIndex: Int // Index, bo ta sama zmiana może być 2 razy w dniu
    ) : SkeletonEditorEvent

    // --- POZIOM 4: DEFINICJA ZMIAN (Tworzenie nowych klocków) ---
    data class CreateShiftTemplate(
        val name: String, // np. "Poranek Barista"
        val startTime: String,
        val endTime: String,
        val role: String,
        val skills: List<String>
    ) : SkeletonEditorEvent

    // Nowe eventy do zarządzania zmianami
    data class ToggleShiftTemplateManager(val isOpen: Boolean) : SkeletonEditorEvent
    data class DeleteShiftTemplate(val templateId: String) : SkeletonEditorEvent
    data class UpdateShiftTemplate(val template: ShiftTemplate) : SkeletonEditorEvent

    /** SH-188: the error was shown; cleared so the same one can show again. */
    data object ErrorShown : SkeletonEditorEvent

    /** SH-190: the note was read (the picker closed); cleared. */
    data object NoticeShown : SkeletonEditorEvent
}

class SkeletonEditorViewModel(
    private val workplaceId: String,
    // Injected UseCases
    private val getSkeletonEditorData: GetSkeletonEditorDataUseCase,
    private val updateWeeklySkeleton: UpdateWeeklySkeletonUseCase,
    private val createDayTemplate: CreateDayTemplateUseCase,
    private val addShiftToDayTemplate: AddShiftToDayTemplateUseCase,
    private val removeShiftFromDayTemplate: RemoveShiftFromDayTemplateUseCase,
    private val createShiftTemplate: CreateShiftTemplateUseCase,
    private val renameDayTemplate: RenameDayTemplateUseCase,
    private val deleteDayTemplate: DeleteDayTemplateUseCase,
    private val duplicateDayTemplate: DuplicateDayTemplateUseCase,
    private val updateShiftTemplate: UpdateShiftTemplateUseCase,
    private val deleteShiftTemplate: DeleteShiftTemplateUseCase,
    private val getWorkplaceUseCase: GetWorkplaceUseCase
) : ViewModel() {

    private val _uiState = MutableStateFlow(SkeletonEditorUiState(workplaceId = workplaceId))
    val uiState = _uiState.asStateFlow()

    /** Set after the first successful load: later loads keep the editor on screen. */
    private var loaded = false

    /** Placeholder ids for created templates until the repository returns the real ones. */
    private var pendingCount = 0

    init {
        loadData()
    }

    fun onEvent(event: SkeletonEditorEvent) {
        when (event) {
            is SkeletonEditorEvent.LoadData -> loadData()
            SkeletonEditorEvent.ErrorShown -> _uiState.update { it.copy(error = null) }
            SkeletonEditorEvent.NoticeShown -> _uiState.update { it.copy(notice = null) }

            is SkeletonEditorEvent.AssignTemplateToDayOfWeek -> {
                val currentSkeleton = _uiState.value.weekSkeleton.toMutableMap()
                if (event.dayTemplateId == null) currentSkeleton.remove(event.dayIso)
                else currentSkeleton[event.dayIso] = event.dayTemplateId
                saveOptimistically({ it.copy(weekSkeleton = currentSkeleton) }) {
                    updateWeeklySkeleton(workplaceId, event.dayIso, event.dayTemplateId)
                }
            }

            is SkeletonEditorEvent.CreateDayTemplate -> {
                val pendingId = nextPendingId()
                saveOptimistically(
                    change = { it.copy(availableDayTemplates = it.availableDayTemplates + DayTemplate(pendingId, event.name, emptyList())) },
                    save = { createDayTemplate(workplaceId, event.name) },
                    onSuccess = { newId: String ->
                        replaceDayTemplateId(pendingId, newId)
                        // Automatycznie otwórz edycję nowo utworzonego szablonu (z prawdziwym id, bo edycja go zapisuje)
                        _uiState.update { it.copy(selectedDayTemplateId = newId) }
                    }
                )
            }

            is SkeletonEditorEvent.SelectDayTemplateForEditing ->
                _uiState.update { it.copy(selectedDayTemplateId = event.templateId) }

            is SkeletonEditorEvent.AddShiftToDayTemplate -> {
                saveOptimistically({ it.withDayTemplate(event.dayTemplateId) { t -> t.copy(shiftTemplateIds = t.shiftTemplateIds + event.shiftTemplateId) } }) {
                    addShiftToDayTemplate(workplaceId, event.dayTemplateId, event.shiftTemplateId)
                }
            }

            is SkeletonEditorEvent.RemoveShiftFromDayTemplate -> {
                saveOptimistically({
                    it.withDayTemplate(event.dayTemplateId) { t ->
                        if (event.shiftTemplateIndex in t.shiftTemplateIds.indices) {
                            t.copy(shiftTemplateIds = t.shiftTemplateIds.toMutableList().apply { removeAt(event.shiftTemplateIndex) })
                        } else t
                    }
                }) {
                    removeShiftFromDayTemplate(workplaceId, event.dayTemplateId, event.shiftTemplateIndex)
                }
            }

            is SkeletonEditorEvent.CreateShiftTemplate -> {
                val pendingId = nextPendingId()
                val startTime = event.startTime.normalizeTime()
                val endTime = event.endTime.normalizeTime()
                // No role picked is no role (null, shown as "No Role"), not an empty text.
                val role = event.role.takeIf { it.isNotBlank() }
                val template = ShiftTemplate(pendingId, event.name, startTime, endTime, role, event.skills)
                saveOptimistically(
                    change = { it.copy(availableShiftTemplates = it.availableShiftTemplates + template) },
                    save = { createShiftTemplate(workplaceId, event.name, startTime, endTime, role, event.skills) },
                    onSuccess = { newShiftId: String ->
                        _uiState.update { state ->
                            state.copy(availableShiftTemplates = state.availableShiftTemplates.map {
                                if (it.id == pendingId) it.copy(id = newShiftId) else it
                            })
                        }
                        // Jeśli jesteśmy w kontekście edycji dnia (selectedDayTemplateId != null),
                        // dodaj nowo utworzoną zmianę automatycznie do tego dnia
                        val currentDayTemplateId = _uiState.value.selectedDayTemplateId
                        if (currentDayTemplateId != null) {
                            onEvent(SkeletonEditorEvent.AddShiftToDayTemplate(currentDayTemplateId, newShiftId))
                        }
                    }
                )
            }

            is SkeletonEditorEvent.RenameDayTemplate -> {
                saveOptimistically({ it.withDayTemplate(event.templateId) { t -> t.copy(name = event.newName) } }) {
                    renameDayTemplate(workplaceId, event.templateId, event.newName)
                }
            }

            is SkeletonEditorEvent.DeleteDayTemplate -> {
                // The use case also clears the weekdays that pointed at it: mirror that here.
                saveOptimistically({ state ->
                    state.copy(
                        availableDayTemplates = state.availableDayTemplates.filterNot { it.id == event.templateId },
                        weekSkeleton = state.weekSkeleton.filterValues { it != event.templateId },
                        selectedDayTemplateId = state.selectedDayTemplateId.takeUnless { it == event.templateId }
                    )
                }) {
                    deleteDayTemplate(workplaceId, event.templateId)
                }
            }

            is SkeletonEditorEvent.DuplicateDayTemplate -> {
                val original = _uiState.value.availableDayTemplates.find { it.id == event.templateId }
                val pendingId = nextPendingId()
                saveOptimistically(
                    change = { state ->
                        // Same name as DuplicateDayTemplateUseCase gives it; the re-read confirms it.
                        if (original == null) state
                        else state.copy(availableDayTemplates = state.availableDayTemplates + original.copy(id = pendingId, name = "${original.name} (Copy)"))
                    },
                    save = { duplicateDayTemplate(workplaceId, event.templateId) },
                    onSuccess = { newId: String ->
                        replaceDayTemplateId(pendingId, newId)
                        loadData() // quiet: picks up the copy as the server made it
                    }
                )
            }

            is SkeletonEditorEvent.ToggleShiftTemplateManager -> {
                _uiState.update { it.copy(isManagingShiftTemplates = event.isOpen) }
            }

            is SkeletonEditorEvent.DeleteShiftTemplate -> {
                // SH-190: it goes out of every day template that uses it too (one batch), the open
                // one included; a failure puts both back with the reason.
                val name = _uiState.value.availableShiftTemplates.find { it.id == event.templateId }?.name
                val openDay = _uiState.value.availableDayTemplates.find { it.id == _uiState.value.selectedDayTemplateId }
                val noteRes = if (openDay != null && event.templateId in openDay.shiftTemplateIds) {
                    Res.string.skeleton_shift_template_deleted_from_day
                } else {
                    Res.string.skeleton_shift_template_deleted
                }
                saveOptimistically({ state ->
                    state.copy(
                        availableShiftTemplates = state.availableShiftTemplates.filterNot { it.id == event.templateId },
                        availableDayTemplates = state.availableDayTemplates.map { it.withoutShiftTemplate(event.templateId) },
                        notice = name?.let { UiText.Resource(noteRes, listOf(it)) }
                    )
                }) {
                    deleteShiftTemplate(workplaceId, event.templateId)
                }
            }

            is SkeletonEditorEvent.UpdateShiftTemplate -> {
                val normalizedTemplate = event.template.copy(
                    startTime = event.template.startTime.normalizeTime(),
                    endTime = event.template.endTime.normalizeTime(),
                    role = event.template.role?.takeIf { it.isNotBlank() }
                )
                saveOptimistically({ state ->
                    state.copy(availableShiftTemplates = state.availableShiftTemplates.map {
                        if (it.id == normalizedTemplate.id) normalizedTemplate else it
                    })
                }) {
                    updateShiftTemplate(workplaceId, normalizedTemplate)
                }
            }
        }
    }

    /**
     * Shows [change] at once, then runs [save]. A failed save puts the week and the templates
     * back as they were and says why; [onSuccess] gets the saved value (e.g. a generated id).
     */
    private fun <T> saveOptimistically(
        change: (SkeletonEditorUiState) -> SkeletonEditorUiState,
        onSuccess: (T) -> Unit = {},
        save: suspend () -> Result<T>
    ) {
        val before = _uiState.value
        _uiState.update(change)
        viewModelScope.launch {
            save().fold(
                onSuccess = onSuccess,
                onFailure = { e -> rollBack(before, e) }
            )
        }
    }

    /** Undoes an optimistic change whose save failed, and says why. */
    private fun rollBack(before: SkeletonEditorUiState, e: Throwable) {
        _uiState.update {
            it.copy(
                weekSkeleton = before.weekSkeleton,
                availableDayTemplates = before.availableDayTemplates,
                availableShiftTemplates = before.availableShiftTemplates,
                notice = before.notice,
                error = e.toUiText()
            )
        }
    }

    private fun nextPendingId(): String = "$PENDING_ID_PREFIX${pendingCount++}"

    private fun replaceDayTemplateId(pendingId: String, newId: String) {
        _uiState.update { state ->
            state.copy(availableDayTemplates = state.availableDayTemplates.map {
                if (it.id == pendingId) it.copy(id = newId) else it
            })
        }
    }

    private fun SkeletonEditorUiState.withDayTemplate(id: String, transform: (DayTemplate) -> DayTemplate) =
        copy(availableDayTemplates = availableDayTemplates.map { if (it.id == id) transform(it) else it })

    /** Full load. Only the first one shows the spinner; later ones refresh in place. */
    private fun loadData() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = !loaded) }

            val workplace = getWorkplaceUseCase(workplaceId).getOrNull()
            val firstDayOfWeek = workplace?.settings?.firstDayOfWeek ?: 1

            getSkeletonEditorData(workplaceId)
                .onSuccess { data ->
                    loaded = true
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            weekSkeleton = data.weekSkeleton,
                            availableDayTemplates = data.dayTemplates,
                            availableShiftTemplates = data.shiftTemplates,
                            availableRoles = data.availableRoles,
                            availableSkills = data.availableSkills,
                            firstDayOfWeek = firstDayOfWeek
                        )
                    }
                }
                .onFailure { handleError(it) }
        }
    }

    private fun handleError(e: Throwable) {
        _uiState.update { it.copy(isLoading = false, error = e.toUiText()) }
    }
}
